import { Brand, logoUrl, platformName } from "./Brand";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { advanceTiles, sceneInterval, featuredFrame } from "./wall-playlist";
import type { Design, Layer, Media, Snapshot } from "./types";
import {
  cacheSnapshot,
  cachedSnapshot,
  clearCache,
  resolveCached,
} from "./cache";
export function Wall({
  name,
  design,
  photos,
  assets,
  paused = false,
  selected,
  onSelect,
  onMove,
}: {
  name: string;
  design: Design;
  photos: Media[];
  assets: Media[];
  paused?: boolean;
  selected?: string;
  onSelect?: (id: string) => void;
  onMove?: (id: string, x: number, y: number) => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState<Media[]>([]);
  const [showExits, setShowExits] = useState(false);
  const [previous, setPrevious] = useState<Media[]>([]);
  const [scale, setScale] = useState(1);
  const pointer = useRef<{
    id: string;
    x: number;
    y: number;
    ox: number;
    oy: number;
  } | null>(null);
  useEffect(() => {
    const observer = new ResizeObserver((entries) =>
      setScale(entries[0].contentRect.width / design.width),
    );
    if (root.current) observer.observe(root.current);
    return () => observer.disconnect();
  }, [design.width]);
  const featured =
    design.layout === "centerstage" ||
    design.layout === "filmstrip" ||
    design.layout === "spotlight";
  const count = design.count;
  const latestPhotos = useRef(photos);
  latestPhotos.current = photos;
  const currentScene = useRef<Media[]>([]);
  const lastNewest = useRef<string | undefined>(undefined);
  const hasPhotos = photos.length > 0;
  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setStep((s) => s + 1),
      sceneInterval(design.duration, design.transitionMs, design.transition),
    );
    return () => clearInterval(id);
  }, [design.duration, design.transitionMs, design.transition, paused]);
  useEffect(() => {
    if (paused && currentScene.current.length) return;
    let active = true;
    const cleanups: (() => void)[] = [];
    const list = latestPhotos.current;
    const newest = list.at(-1)?.id;
    const chosen = advanceTiles(
      list,
      currentScene.current,
      count,
      featured,
      step,
    );
    void Promise.all(
      chosen.map(
        (p) =>
          new Promise<Media | null>((resolve) => {
            const image = new Image();
            const finish = (photo: Media | null) => {
              clearTimeout(timeout);
              image.onload = null;
              image.onerror = null;
              resolve(photo);
            };
            const timeout = setTimeout(() => finish(null), 10000);
            image.onload = () => finish(p);
            image.onerror = () => finish(null);
            cleanups.push(() => {
              finish(null);
            });
            image.src = p.url;
          }),
      ),
    ).then((items) => {
      if (!active) return;
      const loaded = items.filter((m): m is Media => !!m);
      if (!loaded.length) return;
      lastNewest.current = newest;
      if (
        loaded.length === currentScene.current.length &&
        loaded.every(
          (p, i) =>
            p.id === currentScene.current[i].id &&
            p.url === currentScene.current[i].url,
        )
      )
        return;
      setShowExits(true);
      setPrevious(currentScene.current);
      currentScene.current = loaded;
      setReady(loaded);
    });
    return () => {
      active = false;
      cleanups.forEach((cleanup) => cleanup());
    };
    // Snapshot polling only updates the pending playlist. Advance at the next hold boundary.
  }, [step, count, featured, paused, hasPhotos, design.layout]);
  useEffect(() => {
    if (!previous.length || paused) return;
    const timer = setTimeout(
      () => setShowExits(false),
      design.transitionMs + 100,
    );
    return () => clearTimeout(timer);
  }, [previous, design.transitionMs, paused]);
  const background = assets.find((a) => a.id === design.backgroundAsset);
  const tileStyle: CSSProperties = {
    borderRadius: design.radius * scale,
    boxShadow: design.shadow ? "0 12px 30px #0005" : "none",
  };
  function frame(index: number, total: number): CSSProperties {
    if (featured)
      return Object.fromEntries(
        Object.entries(
          featuredFrame(
            index,
            total,
            design.layout === "filmstrip",
            (design.width * design.photoWidth) /
              (design.height * design.photoHeight) <
              1.15,
          ),
        ).map(([k, v]) => [k, `${v}%`]),
      );
    const columns = design.layout === "spotlight" ? 1 : design.columns,
      rows = Math.ceil(total / columns),
      gap = design.gap * scale;
    return {
      left: `calc(${((index % columns) * 100) / columns}% + ${gap / 2}px)`,
      top: `calc(${(Math.floor(index / columns) * 100) / rows}% + ${gap / 2}px)`,
      width: `calc(${100 / columns}% - ${gap}px)`,
      height: `calc(${100 / rows}% - ${gap}px)`,
    };
  }
  function group() {
    const currentIds = new Set(ready.map((p) => p.id));
    const previousIds = new Set(previous.map((p) => p.id));
    const exiting =
      design.transition === "cut" || !showExits
        ? []
        : previous.filter((p) => !currentIds.has(p.id));
    return (
      <div
        className={`photo-group individual-photos layout-${design.layout} transition-${design.transition}`}
        style={
          {
            left: design.photoX + "%",
            top: design.photoY + "%",
            width: design.photoWidth + "%",
            height: design.photoHeight + "%",
            zIndex: design.photoZ,
            "--transition": `${design.transition === "cut" ? 0 : design.transitionMs}ms`,
            "--motion-blur": `${(design.motionBlur ?? 4) * scale}px`,
            "--drift-duration": `${Math.max(20, design.duration * 2)}s`,
          } as CSSProperties
        }
      >
        {[...ready, ...exiting].map((p) => {
          const old = !currentIds.has(p.id),
            index = old
              ? previous.findIndex((o) => o.id === p.id)
              : ready.findIndex((o) => o.id === p.id);
          const promoted =
            featured &&
            index === 0 &&
            previousIds.has(p.id) &&
            previous[0]?.id !== p.id;
          return (
            <div
              key={p.id}
              data-photo-id={p.id}
              data-slot={index}
              aria-hidden={old || undefined}
              className={`photo-tile ${featured ? (index === 0 ? "hero-photo" : "satellite-photo") : ""} ${old ? "tile-exit" : !previousIds.has(p.id) ? "tile-enter" : promoted ? "tile-promote" : ""}`}
              style={{
                ...tileStyle,
                ...({"--tile-opacity":featured&&index>0?.78:1} as CSSProperties),
                ...frame(index, old ? previous.length : ready.length),
                zIndex: old ? 4 : featured && index === 0 ? 3 : 1,
                ...(["floating", "collage"].includes(design.layout)
                  ? { transform: `rotate(${((index % 3) - 1) * 4}deg)` }
                  : {}),
              }}
            >
              {featured && index === 0 && (
                <img
                  key="backdrop"
                  className="hero-backdrop"
                  src={p.url}
                  alt=""
                  aria-hidden="true"
                />
              )}
              <img
                key="photo"
                className="photo-image"
                src={p.url}
                alt={p.name}
                style={{
                  objectFit: featured && index > 0 ? "cover" : design.fit,
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }
  const backgroundStyle: CSSProperties = {
    objectFit: design.backgroundFit ?? "cover",
    objectPosition: "50% 50%",
    transform: `translate(${(design.backgroundX ?? 50) - 50}%,${(design.backgroundY ?? 50) - 50}%) scale(${(design.backgroundScale ?? 100) / 100})`,
    filter: `blur(${(design.backgroundBlur ?? 0) * scale}px)`,
  };
  return (
    <div
      className={`wall motion-${design.motion ?? "gentle"} ${paused ? "wall-paused" : ""}`}
      ref={root}
      style={{
        aspectRatio: `${design.width}/${design.height}`,
        background: design.backgroundColor,
      }}
      onPointerMove={(e) => {
        const p = pointer.current;
        if (!p || !root.current) return;
        const r = root.current.getBoundingClientRect();
        onMove?.(
          p.id,
          Math.max(0, Math.min(99, p.ox + ((e.clientX - p.x) / r.width) * 100)),
          Math.max(
            0,
            Math.min(99, p.oy + ((e.clientY - p.y) / r.height) * 100),
          ),
        );
      }}
      onPointerUp={() => (pointer.current = null)}
    >
      {background &&
        (background.mime.startsWith("video/") ? (
          <video
            className="wall-bg"
            style={backgroundStyle}
            src={background.url}
            autoPlay
            loop
            muted
            playsInline
          />
        ) : (
          <img
            className="wall-bg"
            style={backgroundStyle}
            src={background.url}
            alt=""
          />
        ))}
      {design.showTitle && (
        <div
          className="wall-title"
          style={{ fontSize: Math.max(10, 38 * scale) }}
        >
          <img className="wall-brand" src={logoUrl} alt={platformName} />
          {name || "Your event, live."}
        </div>
      )}
      {ready.length > 0 ? (
        group()
      ) : (
        <div className="wall-empty">
          <img className="wall-logo" src={logoUrl} alt={platformName} />
          <span>{name || "Every moment deserves a bigger stage."}</span>
          <small>New photos will appear here automatically</small>
        </div>
      )}
      {design.layers
        .filter((l) => l.visible)
        .map((l) => {
          const asset = assets.find((a) => a.id === l.assetId);
          return (
            <div
              key={l.id}
              className={`wall-layer ${selected === l.id ? "selected" : ""}`}
              onPointerDown={(e) => {
                if (!onMove) return;
                e.stopPropagation();
                onSelect?.(l.id);
                e.currentTarget.setPointerCapture(e.pointerId);
                pointer.current = {
                  id: l.id,
                  x: e.clientX,
                  y: e.clientY,
                  ox: l.x,
                  oy: l.y,
                };
              }}
              style={{
                left: l.x + "%",
                top: l.y + "%",
                width: l.width + "%",
                height: l.height + "%",
                transform: `rotate(${l.rotation}deg)`,
                opacity: l.opacity,
                zIndex: l.z,
                color: l.color,
                fontSize: l.fontSize * scale,
                cursor: onMove ? "move" : undefined,
              }}
            >
              {l.type === "text"
                ? l.text
                : asset && <img src={asset.url} alt="" draggable={false} />}
            </div>
          );
        })}
    </div>
  );
}
export function PublicPage({
  kind,
  token,
}: {
  kind: "display" | "gallery";
  token: string;
}) {
  const [data, setData] = useState<Snapshot | null>(null),
    [connection, setConnection] = useState("Connecting"),
    [error, setError] = useState(""),
    [lightbox, setLightbox] = useState<Media | null>(null);
  const key = kind + ":" + token;
  const blobUrls = useRef<string[]>([]);
  const [controls, setControls] = useState(true);
  useEffect(() => {
    let active = true,
      timeout: ReturnType<typeof setTimeout>,
      socket: WebSocket,
      attempt = 0,
      fetching = false,
      caching = false,
      revoked = false,
      offlineHydrated = false,
      receivedFresh = false;
    const urls = blobUrls.current;
    const hydrate = async (snapshot: Snapshot) => {
      const all = await Promise.all(
        [...snapshot.photos, ...snapshot.assets].map(async (m) => {
          const url = await resolveCached(key, m.id, m.url);
          if (url.startsWith("blob:")) urls.push(url);
          return { ...m, url };
        }),
      );
      if (active)
        setData({
          ...snapshot,
          photos: all.slice(0, snapshot.photos.length),
          assets: all.slice(snapshot.photos.length),
        });
    };
    const refresh = async () => {
      if (fetching || revoked) return;
      fetching = true;
      try {
        const r = await fetch(`/api/public/${kind}/${token}`, {
          signal: AbortSignal.timeout(8000),
        });
        if (r.status === 404) {
          revoked = true;
          if (active) {
            setData(null);
            setError("This event link is unavailable.");
          }
          await clearCache(key);
          socket?.close();
          return;
        }
        if (!r.ok) throw new Error();
        const next: Snapshot = await r.json();
        receivedFresh = true;
        offlineHydrated = false;
        if (active) {
          setData((old) =>
            old && JSON.stringify(old) === JSON.stringify(next) ? old : next,
          );
          setConnection("Live");
          setError("");
        }
        if (!caching) {
          caching = true;
          void cacheSnapshot(key, next).finally(() => (caching = false));
        }
      } catch {
        if (active) {
          setConnection("Reconnecting · cached photos");
          if (!offlineHydrated) {
            offlineHydrated = true;
            const cached = await cachedSnapshot(key);
            if (cached) await hydrate(cached);
          }
        }
      } finally {
        fetching = false;
      }
    };
    void cachedSnapshot(key)
      .then((s) => {
        if (s && active && !receivedFresh) return hydrate(s);
      })
      .then(refresh);
    const connect = () => {
      if (!active || revoked) return;
      socket = new WebSocket(
        `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/live?kind=${kind}&token=${token}`,
      );
      socket.onopen = () => {
        attempt = 0;
        void refresh();
      };
      socket.onmessage = () => void refresh();
      socket.onclose = () => {
        if (active && !revoked) {
          setConnection("Reconnecting · cached photos");
          timeout = setTimeout(
            connect,
            Math.min(30000, 1000 * 2 ** attempt++) + Math.random() * 500,
          );
        }
      };
      socket.onerror = () => socket.close();
    };
    connect();
    const poll = setInterval(refresh, 15000);
    window.addEventListener("online", refresh);
    return () => {
      active = false;
      clearTimeout(timeout);
      clearInterval(poll);
      socket?.close();
      window.removeEventListener("online", refresh);
      urls.forEach(URL.revokeObjectURL);
    };
  }, [key, kind, token]);
  useEffect(() => {
    if (kind === "gallery") return;
    const timer = setTimeout(() => setControls(false), 4000);
    return () => clearTimeout(timer);
  }, [controls, kind]);
  if (error)
    return (
      <main className="public-message">
        <Brand />
        <h1>Link unavailable</h1>
        <p>{error}</p>
      </main>
    );
  if (!data)
    return (
      <main className="public-message">
        <Brand />
        <p>{connection}…</p>
        <small>
          Open this link while connected to the event server to load photos.
        </small>
      </main>
    );
  if (kind === "gallery")
    return (
      <main className="guest-gallery">
        <header>
          <Brand />
          <span className="pill">{connection}</span>
        </header>
        <p className="eyebrow">YOUR EVENT, YOUR MEMORIES</p>
        <h1>{data.name}</h1>
        <p>Find your favourite moment. Keep it forever.</p>
        <div className="guest-grid">
          {data.photos.map((p) => (
            <button key={p.id} onClick={() => setLightbox(p)}>
              <img src={p.url} alt={p.name} loading="lazy" />
            </button>
          ))}
        </div>
        {!data.photos.length && (
          <div className="empty">
            The first moments are on their way. Photos appear here
            automatically.
          </div>
        )}
        {lightbox && (
          <div className="lightbox" onClick={() => setLightbox(null)}>
            <button className="close">Close ✕</button>
            <img src={lightbox.url} alt={lightbox.name} />
            <a
              className="button primary"
              href={lightbox.originalUrl}
              download
              onClick={(e) => e.stopPropagation()}
            >
              Download original
            </a>
          </div>
        )}
      </main>
    );
  return (
    <main
      className="display-page"
      onMouseMove={() => setControls(true)}
      onClick={() => {
        if (!document.fullscreenElement)
          void document.documentElement.requestFullscreen().catch(() => {});
      }}
    >
      <div
        className="display-fit"
        style={{
          width: `min(100vw, ${(100 * data.design.width) / data.design.height}vh)`,
        }}
      >
        <Wall {...data} paused={data.status === "paused"} />
      </div>
      <div className={`display-controls ${controls ? "" : "hidden"}`}>
        <span>{connection}</span>
        <span>Click for fullscreen · Esc to exit</span>
      </div>
    </main>
  );
}
